import React, { useState } from 'react';
import ShipDateDropdown from '../../sharedComponents/ShipDateDropdown';
import WeeklyShipService from './WeeklyShipService';
import { toast } from 'react-toastify';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { getAirItemTypeLabel } from '../../utils/airItemTypeUtils';

const formatMoney = (n) => (n != null && !Number.isNaN(n) ? Number(n).toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-');

const Row = ({ box }) => {
  const [open, setOpen] = useState(false);
  const orders = box.orders || [];
  const costItems = box.actualCostItems || [];

  return (
    <>
      <TableRow sx={{ '& > *': { borderBottom: 'unset' } }}>
        <TableCell padding="none" sx={{ width: 48 }}>
          <IconButton size="small" onClick={() => setOpen(!open)} aria-label={open ? 'ซ่อนรายละเอียด' : 'แสดงรายละเอียด'}>
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell>{box.boxNo}</TableCell>
        <TableCell>
          {orders.length === 0 ? (
            '-'
          ) : (
            <Box component="span" sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {orders.map((o) => (
                <a key={o.orderId} href={o.url || '#'} target="_blank" rel="noopener noreferrer" style={{ color: 'blue', textDecoration: 'underline', fontSize: '0.8rem', marginRight: 8 }}>
                  Link
                </a>
              ))}
            </Box>
          )}
        </TableCell>
        <TableCell align="right">{formatMoney(box.chargeTotalPerBox)}</TableCell>
        <TableCell align="right">{formatMoney(box.costTotalPerBox)}</TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={5}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 2 }}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2">
                  กล่อง {box.boxNo} {box.weight != null ? `${box.weight} kg` : ''}
                </Typography>
                <Box component="span" sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 0.5 }}>
                  {orders.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">ไม่มี Link</Typography>
                  ) : (
                    orders.map((o) => (
                      <a key={o.orderId} href={o.url || '#'} target="_blank" rel="noopener noreferrer" style={{ color: 'blue', textDecoration: 'underline', fontSize: '0.8rem' }}>Link</a>
                    ))
                  )}
                </Box>
              </Box>
              <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                คิดน้ำหนักลูกค้า (weightPrice)
              </Typography>
              <Table size="small" sx={{ mb: 2 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>ชนิด</TableCell>
                    <TableCell align="right">น้ำหนัก (kg) / จำนวน</TableCell>
                    <TableCell align="right">คิดน้ำหนัก (บาท)</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {orders.map((o) => (
                    <TableRow key={o.orderId}>
                      <TableCell>
                        {(o.chargeTypes || []).length > 0
                          ? (o.chargeTypes || []).map((t) => getAirItemTypeLabel(t)).join(', ')
                          : '-'}
                      </TableCell>
                      <TableCell align="right">
                        {o.weight != null && o.weight !== '' ? `${o.weight} kg` : '-'}
                      </TableCell>
                      <TableCell align="right">{formatMoney(o.weightPrice)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                ต้นทุนจริง (Cost)
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>ชนิด</TableCell>
                    <TableCell align="right">น้ำหนัก (kg) / จำนวน</TableCell>
                    <TableCell align="right">ต้นทุน (บาท)</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {costItems.map((it) => (
                    <TableRow key={it.id || it.airItemTypeId}>
                      <TableCell>{getAirItemTypeLabel(it.airItemType)}</TableCell>
                      <TableCell align="right">
                        {it.airItemType?.costCalculationMethod === 'per_kg' ? `${it.weightKg ?? '-'} kg` : `qty: ${it.qty ?? 1}`}
                      </TableCell>
                      <TableCell align="right">{formatMoney(it.costAmount)}</TableCell>
                    </TableRow>
                  ))}
                  {costItems.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={3} align="center" sx={{ color: 'text.secondary' }}>ยังไม่มีรายการต้นทุน</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

const WeeklyShipAirSummary = () => {
  const [shipDateId, setShipDateId] = useState('');
  const [data, setData] = useState({ boxes: [], summary: { totalCharge: 0, totalCost: 0 } });
  const [loading, setLoading] = useState(false);

  const handleShipDateChange = (e) => {
    const id = e.target.value;
    setShipDateId(id);
    if (id === '' || id === 'all' || id === 'none') {
      setData({ boxes: [], summary: { totalCharge: 0, totalCost: 0 } });
      return;
    }
    setLoading(true);
    WeeklyShipService.getWeeklyShipAirSummaryByShipDateId(parseInt(id, 10))
      .then((res) => {
        const payload = res.data;
        setData({
          boxes: Array.isArray(payload.boxes) ? payload.boxes : [],
          summary: payload.summary || { totalCharge: 0, totalCost: 0 }
        });
      })
      .catch((err) => {
        toast.error(err?.response?.data?.message || 'Failed to load air summary');
        setData({ boxes: [], summary: { totalCharge: 0, totalCost: 0 } });
      })
      .finally(() => setLoading(false));
  };

  const { boxes, summary } = data;

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        สรุปแอร์ (คิดลูกค้า vs ต้นทุน)
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: 2 }}>
        <ShipDateDropdown handleDataChange={handleShipDateChange} airOnly value={shipDateId} includeDefault={true} />
      </Box>

      {summary && (summary.totalCharge !== 0 || summary.totalCost !== 0) && (
        <Paper sx={{ p: 2, mb: 2, display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          <Box>
            <Typography variant="caption" color="text.secondary">รวมคิดน้ำหนักลูกค้า (บาท)</Typography>
            <Typography variant="h6">{formatMoney(summary.totalCharge)}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">รวมต้นทุน (บาท)</Typography>
            <Typography variant="h6">{formatMoney(summary.totalCost)}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">ส่วนต่าง (บาท)</Typography>
            <Typography variant="h6" color={summary.totalCharge - summary.totalCost >= 0 ? 'success.main' : 'error.main'}>
              {formatMoney(summary.totalCharge - summary.totalCost)}
            </Typography>
          </Box>
        </Paper>
      )}

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress />
        </Box>
      )}

      {!loading && boxes.length > 0 && (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="none" sx={{ width: 48 }} />
                <TableCell>กล่อง</TableCell>
                <TableCell>Link</TableCell>
                <TableCell align="right">คิดน้ำหนัก (บาท)</TableCell>
                <TableCell align="right">ต้นทุน (บาท)</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {boxes.map((box) => (
                <Row key={box.weeklyShipId} box={box} />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {!loading && shipDateId && boxes.length === 0 && (
        <Typography color="text.secondary">ไม่มีกล่องสำหรับรอบเรือนี้</Typography>
      )}
    </Box>
  );
};

export default WeeklyShipAirSummary;
